<script lang="ts">
  import { CORP, PLATE_COPY, PLATE_NOTICES, ticksToClock, type NodeId } from "@spacecrew/shared";
  import { ship } from "../net/ship.svelte.js";
  import { session } from "../net/session.svelte.js";

  /** `flat` renders the plate as an ordinary panel (the screen-only view and phones). */
  let { flat = false }: { flat?: boolean } = $props();

  /**
   * The instrument overlay: a screen-printed acetate plate clipped over the tube, with a few values
   * projected onto it. It sits ~10 cm in front of the glass, so it parallaxes against the picture as
   * you shift in the seat — and it is only legible from the back of the seat. Lean in and it blurs
   * out of focus and fades, because your eye is on the picture, not the plate.
   *
   * Purely decorative: `pointer-events: none`, `aria-hidden`, never the only place a value appears.
   */
  const snap = $derived(ship.snap);
  const running = $derived(snap?.phase === "running");
  const me = $derived(snap?.crew[session.sessionId]);
  /** The company sometimes prints the reset procedure here instead of on the console. */
  const procedure = $derived(
    snap && snap.breakers.onPlate && snap.breakers.status !== "idle" && me && !me.toNodeId && me.nodeId === (snap.breakers.readoutNodeId as NodeId)
      ? snap.breakers : null,
  );

  /**
   * The pane is a device, not a label: its print is redrawn every so often on a page it chooses, its
   * reception is worse than the tube's, and a status lamp keeps insisting it is fine. Deliberately
   * silent — it is inches from your face and any tone would nag. Anything urgent takes and holds it.
   */
  const PAGES = ["telemetry", "notice", "hull"] as const;
  let page = $state(0);
  let notice = $state<string>(PLATE_NOTICES[0]!);
  let redraw = $state(false);   // the print sweep as a page changes
  let glitch = $state(false);   // a moment of bad reception

  function flick(next = true) {
    redraw = true;
    if (next) { page = (page + 1) % PAGES.length; notice = PLATE_NOTICES[Math.floor(Math.random() * PLATE_NOTICES.length)]!; }
    setTimeout(() => { redraw = false; }, 260);
  }

  $effect(() => {
    if (flat) return;
    let pageTimer = 0, glitchTimer = 0;
    const schedulePage = () => { pageTimer = window.setTimeout(() => { flick(); schedulePage(); }, 9000 + Math.random() * 7000); };
    const scheduleGlitch = () => {
      glitchTimer = window.setTimeout(() => {
        glitch = true;
        window.setTimeout(() => { glitch = false; }, 90 + Math.random() * 220);
        if (Math.random() < 0.35) flick(false); // sometimes the print stutters and redraws itself
        scheduleGlitch();
      }, 3000 + Math.random() * 9000);
    };
    schedulePage(); scheduleGlitch();
    return () => { window.clearTimeout(pageTimer); window.clearTimeout(glitchTimer); };
  });
</script>

<div class="plate" class:flat class:glitch class:redraw aria-hidden={!flat}>
  <span class="tick tl"></span><span class="tick tr"></span><span class="tick bl"></span><span class="tick br"></span>
  <span class="lamp" class:alive={!flat}></span>
  {#if !flat}<span class="refresh" aria-hidden="true"></span>{/if}
  <p class="head">{CORP} · {PLATE_COPY.header}</p>
  {#if procedure || !running || page === 0}
    <dl class="rows">
      {#if running && snap}
        <dt>RESERVE</dt><dd>{snap.reserve.toFixed(0)}<em>%</em></dd>
        <dt>O<sub>2</sub></dt><dd>{snap.o2.toFixed(0)}<em>%</em></dd>
        <dt>ELAPSED</dt><dd>{ticksToClock(snap.tick)}</dd>
      {:else}
        <dt>HULL</dt><dd class="small">{snap?.hullCode || "————-————"}</dd>
        <dt>STATUS</dt><dd class="small">{snap ? "DOCKED" : "AWAITING CREW"}</dd>
      {/if}
    </dl>
  {:else if page === 1}
    <p class="notice">{notice}</p>
  {:else}
    <dl class="rows">
      <dt>HULL</dt><dd class="small">{snap?.hullCode || "————-————"}</dd>
      <dt>CONTRACT</dt><dd class="small">{ticksToClock(snap?.tick ?? 0)} ELAPSED</dd>
      <dt>CREW</dt><dd class="small">{Object.keys(snap?.crew ?? {}).length} ABOARD</dd>
    </dl>
  {/if}
  {#if procedure}
    <div class="procedure">
      <p class="head">BREAKER RESET PROCEDURE — RELAY TO THE BREAKER BAY</p>
      <p class="seq">
        {#each procedure.required as id, i}<span class={i < procedure.flipped.length ? "done" : ""}>{id}</span>{#if i < procedure.required.length - 1} → {/if}{/each}
      </p>
    </div>
  {/if}
  <p class="foot">{PLATE_COPY.footer}</p>
</div>

<style>
  /* `--plate` is set per frame by the station view on the wrapper (1 = at the plate's focal depth).
     It must NOT be declared here: a local declaration would win over the inherited value. */
  .plate {
    position: relative; width: 100%; height: 100%; pointer-events: none;
    display: flex; flex-direction: column; justify-content: space-between;
    padding: 26px 30px; box-sizing: border-box;
    color: rgb(238, 252, 255);
    font-size: 13px; font-weight: 600; letter-spacing: 0.16em; text-transform: uppercase;
    opacity: var(--plate, 0);
    filter: blur(calc((1 - var(--plate, 0)) * 3px)) brightness(2.05) contrast(1.12) saturate(1.1);
    /* the plate's own body: a barely-there sheet of scratched acetate */
    /* A lit sheet, not a transparency: most of the intensity comes from the pane having a surface. */
    background:
      radial-gradient(85% 70% at 38% 38%, rgba(120, 205, 245, 0.08), rgba(60, 140, 180, 0.035) 60%, rgba(20, 60, 85, 0.02) 100%),
      linear-gradient(118deg, rgba(200, 240, 255, 0.035) 0%, rgba(170, 225, 255, 0.012) 26%, rgba(120, 190, 230, 0.012) 62%, rgba(200, 240, 255, 0.025) 100%);
    border-radius: 40px / 50px;
    text-shadow:
      0 0 2px rgba(255, 255, 255, 1),
      0 0 7px rgba(215, 246, 255, 1),
      0 0 18px rgba(150, 220, 255, 0.85),
      0 0 38px rgba(110, 200, 255, 0.55),
      0 0 72px rgba(80, 180, 255, 0.3);
  }
  .head, .foot { margin: 0; font-size: 0.62em; opacity: 0.88; }
  .notice { margin: 0; align-self: flex-start; max-width: 30ch; font-size: 0.82em; line-height: 1.5; }
  /* Status lamp: the device insists it is fine. */
  .lamp { position: absolute; right: 20px; top: 18px; width: 8px; height: 8px; border-radius: 50%;
    background: rgb(150, 255, 200); box-shadow: 0 0 12px rgba(120, 255, 180, 0.95), 0 0 26px rgba(120, 255, 180, 0.5); opacity: 0.3; }
  .lamp.alive { animation: lamp 3.4s ease-in-out infinite; }
  @keyframes lamp { 0%, 44%, 100% { opacity: 0.18; } 48%, 56% { opacity: 1; } }
  /*
   * The pane runs at about 45 Hz: the scanlines crawl rather than sit still, a refresh band beats
   * down the glass, and the vertical hold is not quite right, so the whole print steps a pixel or two
   * every few seconds. Rendered as motion rather than noise — it should read as a device that is
   * running, not as damage.
   */
  .plate::after {
    content: ""; position: absolute; inset: 0; pointer-events: none; border-radius: inherit;
    background: repeating-linear-gradient(0deg,
      rgba(0, 0, 0, 0.16) 0,
      rgba(0, 0, 0, 0.16) 0.6px,
      rgba(0, 0, 0, 0) 1.8px,
      rgba(0, 0, 0, 0) 3px);
    mix-blend-mode: multiply; opacity: 0.5;
    animation: crawl 5s linear infinite;
  }
  .refresh {
    position: absolute; left: 0; right: 0; height: 22%; z-index: 3; pointer-events: none;
    mix-blend-mode: screen; opacity: 0.26;
    background: linear-gradient(180deg, transparent, rgba(205, 245, 255, 0.06) 42%, rgba(240, 253, 255, 0.12) 52%, rgba(205, 245, 255, 0.05) 62%, transparent);
    animation: beat 1.7s linear infinite;
  }
  .plate:not(.flat) { animation: hold 11s steps(1, end) infinite; }
  @keyframes crawl { to { background-position-y: 3px; } }
  @keyframes beat { from { top: -22%; } to { top: 100%; } }
  @keyframes hold {
    0%, 30% { transform: translateY(0); }
    30.6% { transform: translateY(-1px); }
    31.2%, 74% { transform: translateY(0); }
    74.6% { transform: translateY(1px); }
    75.2%, 100% { transform: translateY(0); }
  }
  @media (prefers-reduced-motion: reduce) {
    .plate::after, .refresh, .plate:not(.flat) { animation: none; }
    .refresh { display: none; }
  }
  .plate.glitch { filter: blur(calc((1 - var(--plate, 0)) * 3px)) brightness(1.7) contrast(1.5) saturate(1.2); }
  .plate.glitch .rows, .plate.glitch .notice, .plate.glitch .procedure {
    transform: translateX(-3px) skewX(-1.2deg); opacity: 0.55;
  }
  /* The print sweeps down as a page is redrawn. */
  .plate.redraw::before {
    content: ""; position: absolute; left: 0; right: 0; height: 34%; z-index: 2; pointer-events: none;
    background: linear-gradient(180deg, transparent, rgba(190, 235, 255, 0.16), transparent);
    animation: sweep 260ms linear 1;
  }
  @keyframes sweep { from { top: -34%; } to { top: 100%; } }
  .procedure { position: absolute; left: 0; right: 0; top: 38%; text-align: center; }
  .seq { margin: 0.15em 0 0; font-size: 2.2em; letter-spacing: 0.22em; }
  .seq .done { opacity: 0.35; text-decoration: line-through; }
  /* Inline (screen-only view and phones): no depth to lean out of, so it is simply a panel. */
  .plate.flat { opacity: 1; filter: none; border: 1px solid rgba(190, 235, 255, 0.35); border-radius: 6px;
    padding: 0.7rem 0.9rem; gap: 0.6rem; background: rgba(120, 190, 230, 0.05); height: auto; }
  .plate.flat .rows dd { font-size: 1.5em; }
  .plate.flat .tick { display: none; }
  .foot { align-self: flex-end; }
  /* Etched values sit low-left, out of the picture's way; anything urgent takes the centre. */
  .rows { display: grid; grid-template-columns: max-content auto; align-items: baseline; gap: 0.05em 1.1em; margin: 0; align-self: flex-start; }
  dt { font-size: 0.7em; opacity: 0.92; }
  dd { margin: 0; font-size: 1.35em; line-height: 1; font-variant-numeric: tabular-nums; }
  dd.small { font-size: 1.1em; letter-spacing: 0.24em; }
  em { font-style: normal; font-size: 0.45em; opacity: 0.7; margin-left: 0.15em; }
  sub { font-size: 0.6em; }
  /* engraved corner registration marks */
  .tick { position: absolute; width: 26px; height: 26px; opacity: 0.72; }
  .tick::before, .tick::after { content: ""; position: absolute; background: currentColor; }
  .tick::before { width: 100%; height: 1px; top: 0; }
  .tick::after { width: 1px; height: 100%; left: 0; }
  .tl { top: 14px; left: 16px; }
  .tr { top: 14px; right: 16px; transform: scaleX(-1); }
  .bl { bottom: 14px; left: 16px; transform: scaleY(-1); }
  .br { bottom: 14px; right: 16px; transform: scale(-1); }
</style>
